import { useEffect, useState } from 'react';

import { CACHE_WRITE_ID_KEY, type FullContext } from '../types';

type Input =
	| { enabled: true; cache: FullContext['cache'] }
	| { enabled: false; cache?: FullContext['cache'] };
export const useCanWriteToCache = ({ enabled, cache }: Input) => {
	const [canWrite, setCanWrite] = useState<boolean | null>(null);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		let isMounted = true;
		(async () => {
			const result = await canWriteToCache({ cache });
			if (isMounted) {
				setCanWrite(result);
			}
		})();

		return () => {
			isMounted = false;
		};
	}, [enabled, cache]);

	return canWrite;
};

// Tests if we can actually write to the cache -- not only looks at
// the write status, but does a read back.
// XXX: Needs retries because the browser control's async setCookie() function
// has a bug/limitation when it doesn't correctly wait for the product context
// to init. If we call the function "too early", it says the cookie is blocked,
// even thoughonce the product context loads -- it won't be.
const canWriteToCache = async ({
	maxRetries = 2,
	retry = 0,
	retryDelay = 1000,
	cache,
}: {
	maxRetries?: number;
	retry?: number;
	retryDelay?: number;
} & Pick<FullContext, 'cache'>): Promise<boolean> => {
	try {
		const initialRead = cache.read({ ssr: false });
		const writtenId = (initialRead?.[CACHE_WRITE_ID_KEY] ?? 0) + 1;
		const writeResult = await cache.write({ ...initialRead, [CACHE_WRITE_ID_KEY]: writtenId });
		const wroteSuccessfully =
			writeResult === true && cache.read({ ssr: false })?.[CACHE_WRITE_ID_KEY] === writtenId;

		if (!wroteSuccessfully && retry < maxRetries) {
			await new Promise((resolve) => setTimeout(resolve, retryDelay));
			return await canWriteToCache({ retry: retry + 1, maxRetries, retryDelay, cache });
		}

		return wroteSuccessfully;
	} catch {
		return false;
	}
};
